import { useCallback, useEffect, useRef, useState } from "react";
import { InstantOrderSound, resolveOrderSound } from "../utils/instantOrderSound";

export default function useInstantOrderSound() {
  const [customSound, setCustomSound] = useState(() => localStorage.getItem("instantOrderCustomSound"));
  const [customError, setCustomError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(() => localStorage.getItem("instantOrderSoundEnabled") !== "false");
  const [volume, setVolume] = useState(() => {
    const stored = localStorage.getItem("instantOrderSoundVolume");
    const value = stored === null ? 100 : Number(stored);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 100;
  });
  const [blocked, setBlocked] = useState(true);
  const [preset, setPreset] = useState(() => {
    const stored = localStorage.getItem("instantOrderSoundPreset");
    return stored === "custom" && customSound ? "custom" : resolveOrderSound(stored).id;
  });
  const sound = useRef<InstantOrderSound | null>(null);
  const preferences = useRef({ enabled, volume, preset });
  preferences.current = { enabled, volume, preset };
  useEffect(() => {
    const player = new InstantOrderSound();
    sound.current = player;
    let customLoaded = false;
    let restoring = false;
    const unlock = () => {
      void player.unlock().then(async ready => {
        const saved = localStorage.getItem("instantOrderCustomSound");
        if (ready && saved && !customLoaded && !restoring) {
          restoring = true;
          try { await player.loadCustomSound(saved); customLoaded = true; }
          catch { if (sound.current === player) setCustomError("Could not load custom sound. Upload it again."); }
          finally { restoring = false; }
        }
        if (sound.current === player) setBlocked(!ready);
      });
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      sound.current = null;
      player.close();
    };
  }, []);
  useEffect(() => {
    localStorage.setItem("instantOrderSoundEnabled", String(enabled));
    localStorage.setItem("instantOrderSoundVolume", String(volume));
    localStorage.setItem("instantOrderSoundPreset", preset);
    sound.current?.setVolume(enabled ? volume / 100 : 0);
  }, [enabled, volume, preset]);
  const uploadCustomSound = useCallback(async (file: File) => {
    setCustomError(null);
    if (file.size > 2 * 1024 * 1024) {
      setCustomError("Choose an audio file smaller than 2 MB.");
      return;
    }
    const player = sound.current;
    if (!player) return;
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("File unreadable"));
        reader.readAsDataURL(file);
      });
      await player.loadCustomSound(dataUrl);
      if (sound.current !== player) return;
      localStorage.setItem("instantOrderCustomSound", dataUrl);
      setCustomSound(dataUrl);
      setPreset("custom");
      setBlocked(false);
    } catch {
      if (sound.current === player) setCustomError("Could not save sound. Use a supported audio file under 30 seconds and 2 MB.");
    }
  }, []);
  const play = useCallback((userGesture = false) => {
    const preference = preferences.current;
    const player = sound.current;
    if (!preference.enabled || preference.volume === 0 || !player) return;
    void player.play(preference.volume / 100, userGesture, preference.preset).then(ready => { if (sound.current === player) setBlocked(!ready); })
      .catch(() => { if (sound.current === player) setBlocked(true); });
  }, []);
  return { enabled, setEnabled, volume, setVolume, preset, setPreset, blocked, play, customSound, customError, uploadCustomSound };
}
